import { useState, useCallback } from 'react';
import {
  Button,
  Badge,
  Spinner,
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  ScrollArea,
  Separator,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from '@rnd-payplus/alloy-react';
import { Upload, CheckCircle, XCircle, FileUp, Eye, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { KPICard } from '@rnd-payplus/alloy-react/patterns';
import { usePageShell } from '../contexts/PageShellContext';

interface MappingRule {
  source: string;
  target: string;
  transform?: string;
}

interface StoredMapping {
  id: string;
  name: string;
  source_system: string;
  target_system: string;
  field_mappings: { mappings: MappingRule[] };
  updated_at: string;
}

interface IngestResponse {
  file_name: string;
  file_size: number;
  source: string;
  target: string;
  mappingId: string | null;
  parsed: {
    header: Record<string, unknown>;
    payments: Array<Record<string, unknown>>;
    footer: Record<string, unknown>;
  };
  cdm: {
    data: Record<string, unknown>;
    target: string;
    cdmVersion: string;
  };
  error?: string;
  stage?: string;
}

interface AcceptResponse {
  payment_run_id: string;
  status: string;
  payplus_response: Record<string, unknown>;
  error?: string;
}

function getNestedValue(obj: unknown, path: string): unknown {
  if (!obj || typeof obj !== 'object') return undefined;
  let current: unknown = obj;
  for (const part of path.split('.')) {
    if (current === null || current === undefined || typeof current !== 'object') return undefined;
    if (part.endsWith('[]')) {
      const arr = (current as Record<string, unknown>)[part.slice(0, -2)];
      current = Array.isArray(arr) && arr.length > 0 ? arr[0] : undefined;
    } else {
      current = (current as Record<string, unknown>)[part];
    }
  }
  return current;
}

function groupBy<T>(items: T[], key: (item: T) => string): Record<string, T[]> {
  const groups: Record<string, T[]> = {};
  for (const item of items) {
    const k = key(item);
    (groups[k] ??= []).push(item);
  }
  return groups;
}

function sectionFromTarget(target: string): string {
  const root = target.split('.')[0].replace('[]', '');
  const labels: Record<string, string> = {
    payment_run: 'Payment Run', supplier: 'Supplier', beneficiary: 'Beneficiary',
    payment: 'Payment', payment_instruction: 'Instruction', invoices: 'Invoices',
    invoice_line_items: 'Invoice Line Items', payment_line_items: 'Payment Line Items',
  };
  return labels[root] ?? root;
}

function MappingTable({ rules, parsed, cdmData }: { rules: MappingRule[]; parsed: Record<string, unknown>; cdmData: unknown }) {
  const grouped = groupBy(rules, (r) => sectionFromTarget(r.target));
  return (
    <ScrollArea className="h-[500px]">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[250px]">Source Field</TableHead>
            <TableHead className="w-[40px]" />
            <TableHead className="w-[280px]">CDM Target Field</TableHead>
            <TableHead>Value</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {Object.entries(grouped).map(([section, sectionRules]) => (
            <>
              <TableRow key={`section-${section}`}>
                <TableCell colSpan={4} className="bg-muted/50 font-semibold text-xs uppercase tracking-wide py-1.5">
                  {section}
                </TableCell>
              </TableRow>
              {sectionRules.map((r) => {
                const sourceVal = getNestedValue(parsed, r.source);
                const targetVal = getNestedValue(cdmData, r.target);
                const displayVal = targetVal ?? sourceVal;
                return (
                  <TableRow key={r.source + r.target}>
                    <TableCell className="font-mono text-xs text-muted-foreground">{r.source}</TableCell>
                    <TableCell><ArrowRight className="h-3 w-3 text-muted-foreground" /></TableCell>
                    <TableCell className="font-mono text-xs">{r.target}</TableCell>
                    <TableCell className="text-xs max-w-[200px] truncate">
                      {displayVal != null ? String(displayVal) : <span className="text-muted-foreground italic">null</span>}
                    </TableCell>
                  </TableRow>
                );
              })}
            </>
          ))}
        </TableBody>
      </Table>
    </ScrollArea>
  );
}

function flattenKeys(obj: unknown, prefix = ''): string[] {
  if (obj === null || obj === undefined || typeof obj !== 'object') return [];
  if (Array.isArray(obj)) return obj.length > 0 ? flattenKeys(obj[0], prefix + '[]') : [];
  const keys: string[] = [];
  for (const [k, v] of Object.entries(obj)) {
    const path = prefix ? `${prefix}.${k}` : k;
    if (v !== null && typeof v === 'object') keys.push(...flattenKeys(v, path));
    else keys.push(path);
  }
  return keys;
}

function flattenEntries(obj: unknown, prefix = ''): [string, unknown][] {
  if (obj === null || obj === undefined || typeof obj !== 'object') return [];
  if (Array.isArray(obj)) {
    const entries: [string, unknown][] = [];
    obj.forEach((item, i) => entries.push(...flattenEntries(item, `${prefix}[${i}]`)));
    return entries;
  }
  const entries: [string, unknown][] = [];
  for (const [k, v] of Object.entries(obj)) {
    const path = prefix ? `${prefix}.${k}` : k;
    if (v !== null && typeof v === 'object') entries.push(...flattenEntries(v, path));
    else entries.push([path, v]);
  }
  return entries;
}

function FieldTable({ data }: { data: unknown }) {
  return (
    <ScrollArea className="h-[400px]">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Field</TableHead>
            <TableHead>Value</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {flattenEntries(data).map(([path, val]) => (
            <TableRow key={path}>
              <TableCell className="font-mono text-xs">{path}</TableCell>
              <TableCell className="text-xs max-w-[200px] truncate">{String(val ?? '')}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </ScrollArea>
  );
}

const IngestPage = () => {
  usePageShell({ title: 'Ingest' });

  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [ingestResult, setIngestResult] = useState<IngestResponse | null>(null);
  const [accepting, setAccepting] = useState(false);
  const [acceptResult, setAcceptResult] = useState<AcceptResponse | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [jsonPreviewOpen, setJsonPreviewOpen] = useState(false);
  const [mappingRules, setMappingRules] = useState<MappingRule[]>([]);
  const [mappingName, setMappingName] = useState<string>('');

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.[0]) {
      setFile(e.target.files[0]);
      setIngestResult(null);
      setAcceptResult(null);
    }
  };

  const handleUpload = useCallback(async () => {
    if (!file) { toast.error('Select a file first'); return; }
    setUploading(true);
    setIngestResult(null);
    setAcceptResult(null);
    setMappingRules([]);
    setMappingName('');
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await fetch('/api/ingest', {
        method: 'POST',
        headers: { 'x-api-key': 'chub_dev-test-key', 'X-Tenant-ID': 'default' },
        body: form,
      });
      const data: IngestResponse = await res.json();
      if (!res.ok) {
        toast.error(data.error ?? `Upload failed (${res.status})`);
        return;
      }
      setIngestResult(data);
      toast.success('File parsed and CDM built');

      if (data.mappingId) {
        const mappingsRes = await fetch(`/api/ingest/mappings?source=${encodeURIComponent(data.source)}&target=${encodeURIComponent(data.target)}`);
        if (mappingsRes.ok) {
          const all: StoredMapping[] = await mappingsRes.json();
          const match = all.find((m) => m.id === data.mappingId);
          if (match) {
            setMappingRules(match.field_mappings?.mappings ?? []);
            setMappingName(match.name);
          }
        }
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
    }
  }, [file]);

  const handleAccept = useCallback(async () => {
    if (!ingestResult?.cdm) return;
    setAccepting(true);
    setAcceptResult(null);
    try {
      const res = await fetch('/api/ingest/accept', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          cdm: ingestResult.cdm.data ?? ingestResult.cdm,
          tenant_id: 'default',
        }),
      });
      const data: AcceptResponse = await res.json();
      setAcceptResult(data);
      if (data.status === 'sent') {
        toast.success(`Payment run ${data.payment_run_id} submitted`);
      } else {
        toast.error(`Payment run failed: ${data.payplus_response?.error ?? 'unknown'}`);
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Accept failed');
    } finally {
      setAccepting(false);
      setConfirmOpen(false);
    }
  }, [ingestResult]);

  const cdmData = ingestResult?.cdm?.data ?? ingestResult?.cdm;
  const sourceFields = ingestResult?.parsed ? flattenKeys(ingestResult.parsed) : [];
  const targetFields = cdmData ? flattenKeys(cdmData) : [];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <KPICard title="File" value={ingestResult?.file_name ?? 'None'} />
        <KPICard title="Source Fields" value={String(sourceFields.length)} />
        <KPICard title="Target Fields" value={String(targetFields.length)} />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>1. Upload Payment File</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-4">
            <input
              type="file"
              accept=".xml"
              onChange={handleFileChange}
              className="block text-sm file:mr-4 file:rounded-md file:border-0 file:bg-primary file:px-4 file:py-2 file:text-sm file:font-medium file:text-primary-foreground hover:file:bg-primary/90"
            />
            <Button onClick={handleUpload} disabled={!file || uploading}>
              {uploading ? <Spinner className="mr-2 h-4 w-4" /> : <FileUp className="mr-2 h-4 w-4" />}
              Upload & Parse
            </Button>
          </div>
          {file && !ingestResult && (
            <p className="text-sm text-muted-foreground">{file.name} ({(file.size / 1024).toFixed(1)} KB)</p>
          )}
        </CardContent>
      </Card>

      {ingestResult && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>2. Mapping Preview</CardTitle>
            <div className="flex gap-2 items-center">
              <Badge variant="outline">{ingestResult.source}</Badge>
              <ArrowRight className="h-4 w-4 text-muted-foreground" />
              <Badge variant="outline">{ingestResult.target}</Badge>
              {mappingName && (
                <Badge variant="secondary">{mappingName}</Badge>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="mapping">
              <TabsList>
                <TabsTrigger value="mapping">Field Mapping ({mappingRules.length})</TabsTrigger>
                <TabsTrigger value="source">Source Data</TabsTrigger>
                <TabsTrigger value="target">CDM Data</TabsTrigger>
              </TabsList>

              <TabsContent value="mapping" className="mt-4">
                {mappingRules.length > 0 ? (
                  <MappingTable
                    rules={mappingRules}
                    parsed={ingestResult.parsed as unknown as Record<string, unknown>}
                    cdmData={cdmData}
                  />
                ) : (
                  <p className="text-sm text-muted-foreground py-8 text-center">
                    No mapping selected. The builder used default field mappings.
                  </p>
                )}
              </TabsContent>

              <TabsContent value="source" className="mt-4">
                <FieldTable data={ingestResult.parsed} />
              </TabsContent>

              <TabsContent value="target" className="mt-4">
                <FieldTable data={cdmData} />
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      )}

      {ingestResult && (
        <Card>
          <CardHeader>
            <CardTitle>3. Submit</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4">
              <Button variant="outline" onClick={() => setJsonPreviewOpen(true)}>
                <Eye className="mr-2 h-4 w-4" /> View CDM JSON
              </Button>
              <Button onClick={() => setConfirmOpen(true)} disabled={accepting || !!acceptResult}>
                {accepting ? <Spinner className="mr-2 h-4 w-4" /> : <Upload className="mr-2 h-4 w-4" />}
                Accept & Submit
              </Button>
            </div>

            {acceptResult && (
              <>
                <Separator className="my-4" />
                <div className={`rounded-lg border p-4 ${acceptResult.status === 'sent' ? 'border-green-500/40 bg-green-500/5' : 'border-destructive/40 bg-destructive/5'}`}>
                  <div className="flex items-center gap-2">
                    {acceptResult.status === 'sent'
                      ? <CheckCircle className="h-5 w-5 text-green-600" />
                      : <XCircle className="h-5 w-5 text-destructive" />}
                    <span className="font-medium">
                      {acceptResult.status === 'sent' ? 'Submitted for processing' : 'Failed'}
                    </span>
                  </div>
                  <ul className="mt-2 text-xs font-mono space-y-1">
                    <li>payment_run_id: {acceptResult.payment_run_id}</li>
                    <li>status: {acceptResult.status}</li>
                    {acceptResult.payplus_response?.error ? (
                      <li>error: {String(acceptResult.payplus_response.error)}</li>
                    ) : null}
                  </ul>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      )}

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Submit payment for processing</DialogTitle>
            <DialogDescription>
              This will submit the payment run for processing. Continue?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline" disabled={accepting}>Cancel</Button>
            </DialogClose>
            <Button onClick={handleAccept} disabled={accepting}>
              {accepting ? <Spinner className="mr-2 h-4 w-4" /> : null}
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={jsonPreviewOpen} onOpenChange={setJsonPreviewOpen}>
        <DialogContent className="sm:max-w-3xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>CDM Document</DialogTitle>
          </DialogHeader>
          <div className="overflow-auto max-h-[60vh]">
            <pre className="rounded-md border bg-muted/30 p-4 text-xs font-mono whitespace-pre-wrap break-all">
              {JSON.stringify(cdmData, null, 2)}
            </pre>
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Close</Button>
            </DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default IngestPage;
